import { Grid, Paper, Stack, Typography } from '@mui/material'
import { CarrierType, CarrierVisitDirection } from '@planning/app/api'
import IconTruck from '@planning/components/icon/IconTruck'
import { ChangeTransportSubFormStore } from '@planning/components/organisms/ChangeTransportSubForm'
import { useMutualCarrierTypeCleanup } from '@planning/hooks/useMutualCarrierTypeCleanup'
import { OrderFormStore } from '@planning/pages/Order/V2/stores/OrderFormStore'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  DataControlCard,
  ReferenceWidget,
  TrainIcon,
  VesselIcon,
} from '@tom-ui/ui'
import { formatVesselVisitIdentifier, PATHS, validateCarrierTypes } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { OrderTransportDuplicateContainerWarning } from '../molecules/OrderTransportDuplicateContainerWarning'
import { OrderViewCardProps } from '../steps/OrderReviewStep'
import { OrderTransportCard } from './OrderTransportCard'

type TransportData = {
  direction: CarrierVisitDirection
  carrierType?: CarrierType | null
  carrierName?: string | null
  eta?: string | null
  ata?: string | null
  atd?: string | null
  vesselStowagePosition?: string | null
  vesselNumber?: string | null
  operator?: string | null
  numericReleaseId?: number | null
  numericReleaseReference?: string | null
  nonNumericReleaseId?: number | null
  nonNumericReleaseReference?: string | null
  closedAt?: string | null
  hasDuplicateContainerConflict?: boolean
}

type InboundTransportData = TransportData & {
  portOfLoading?: string | null
  dischargeApproval?: boolean
}

type OutboundTransportData = TransportData & {
  portOfDischarge?: string | null
  finalDestination?: string | null
  loadApproval?: boolean
  isFinalPosition?: boolean
}

export type OrderTransportViewCardAttributes = {
  inboundTransport?: InboundTransportData
  outboundTransport?: OutboundTransportData
}

const TransportCard = observer(
  (props: TransportData & Partial<InboundTransportData & OutboundTransportData>) => {
    const {
      direction,
      carrierName,
      carrierType,
      eta,
      ata,
      atd,
      dischargeApproval,
      loadApproval,
      vesselStowagePosition,
      vesselNumber,
      isFinalPosition,
      closedAt,
      hasDuplicateContainerConflict,
    } = props

    const { t } = useTranslate()

    const isInboundTransport = direction === CarrierVisitDirection.Inbound
    const isOutboundTransport = direction === CarrierVisitDirection.Outbound

    let displayLabelKey = ''
    let displayDate: string | null | undefined = null

    if (atd) {
      displayLabelKey = 'atd'
      displayDate = atd
    } else if (ata) {
      displayLabelKey = 'ata'
      displayDate = ata
    } else if (eta) {
      displayLabelKey = 'eta'
      displayDate = eta
    }

    const getCarrierText = (
      carrierType?: CarrierType,
      carrierName?: string | null | undefined,
      vesselNumber?: string | null | undefined,
    ): string => {
      if (carrierType === CarrierType.Truck) {
        return t('truck', 'Truck')
      }

      if (carrierType === CarrierType.Vessel && carrierName && vesselNumber) {
        return formatVesselVisitIdentifier(carrierName, vesselNumber) ?? carrierName
      }

      return carrierName ?? t('notAssigned', 'Not assigned')
    }

    return (
      <Paper variant='outlined' sx={{ p: '1rem', height: '100%' }}>
        <Grid container direction={'column'}>
          <Grid mb={2} size={12}>
            <Stack direction='row' spacing={1}>
              {isInboundTransport && <ArrowDownRightIcon />}
              {isOutboundTransport && <ArrowUpRightIcon />}
              <Typography variant='subtitle1'>
                {isInboundTransport
                  ? t('inboundTransport', 'Inbound transport')
                  : t('outboundTransport', 'Outbound transport')}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack direction='row' spacing={1}>
              {carrierType === CarrierType.Truck && <IconTruck />}
              {carrierType === CarrierType.Vessel && <VesselIcon />}
              {carrierType === CarrierType.Train && <TrainIcon />}
              <Typography variant='body1'>
                {getCarrierText(carrierType!, carrierName, vesselNumber)}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={12} pl={4.2}>
            {displayDate && (
              <Typography variant='caption'>
                {t(displayLabelKey, displayLabelKey)}: {formatDateTime(displayDate)}
              </Typography>
            )}
          </Grid>
          {closedAt && (
            <Grid size={12} pl={4.2}>
              <Typography variant='caption'>
                {t('checkTime', 'Check time')}: {formatDateTime(closedAt)}
              </Typography>
            </Grid>
          )}
          <Grid size={12} mt={2}>
            <Stack direction='row' pl={4.2} spacing={3}>
              {isInboundTransport && carrierType !== CarrierType.Truck && dischargeApproval && (
                <Stack>
                  <Typography variant='body2'>
                    {t('dischargeApproval', 'Discharge approval')}
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    {dischargeApproval ? t('yes', 'Yes') : t('no', 'No')}
                  </Typography>
                </Stack>
              )}

              {isOutboundTransport && carrierType !== CarrierType.Truck && loadApproval && (
                <Stack>
                  <Typography variant='body2'>{t('loadApproval', 'Load approval')}</Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    {loadApproval ? t('yes', 'Yes') : t('no', 'No')}
                  </Typography>
                </Stack>
              )}

              {vesselStowagePosition && (
                <Stack>
                  <Typography variant='body2'>
                    {isFinalPosition
                      ? t('actualPosition', 'Actual position')
                      : t('positionOnVessel', 'Position on vessel')}
                  </Typography>
                  <Typography variant='body1' fontWeight='bold'>
                    {vesselStowagePosition}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Grid>
          <ReleaseReference {...props} />
          {hasDuplicateContainerConflict && (
            <Grid size={12} mt={2}>
              <OrderTransportDuplicateContainerWarning />
            </Grid>
          )}
        </Grid>
      </Paper>
    )
  },
)

const ReleaseReference: FC<TransportData & Partial<InboundTransportData & OutboundTransportData>> =
  observer(props => {
    const { t } = useTranslate()

    const {
      numericReleaseId,
      numericReleaseReference,
      nonNumericReleaseId,
      nonNumericReleaseReference,
      operator,
    } = props

    const isNumericRelease = !!numericReleaseId
    const isNonNumericRelease = !!nonNumericReleaseId

    if (!isNumericRelease && !isNonNumericRelease) return

    const title = isNumericRelease
      ? t('numericRelease', 'Numeric Release')
      : t('nonNumericRelease', 'Non-Numeric Release')

    const onPrimaryAction = isNumericRelease
      ? () => window.open(`${PATHS.orders.numericReleases.url}/${numericReleaseId}/details`)
      : () => window.open(`${PATHS.orders.nonNumericReleases.url}/${nonNumericReleaseId}/details`)

    const reference = isNumericRelease ? numericReleaseReference : nonNumericReleaseReference

    return (
      <ReferenceWidget
        title={title}
        reference={reference || t('notAssigned', 'Not assigned')}
        infoText={operator}
        onPrimaryAction={onPrimaryAction}
      />
    )
  })

const TransportViewMode = observer(
  (props: {
    attributes: OrderTransportViewCardAttributes
    inboundTransportStore?: ChangeTransportSubFormStore
    outboundTransportStore?: ChangeTransportSubFormStore
  }) => {
    const { inboundTransport, outboundTransport } = props.attributes
    const { inboundTransportStore, outboundTransportStore } = props

    return (
      <Grid container spacing={2}>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <TransportCard
            direction={CarrierVisitDirection.Inbound}
            {...inboundTransport}
            hasDuplicateContainerConflict={inboundTransportStore?.hasDuplicateContainerConflict}
          />
        </Grid>
        <Grid
          size={{
            sm: 12,
            md: 6,
          }}
        >
          <TransportCard
            direction={CarrierVisitDirection.Outbound}
            {...outboundTransport}
            hasDuplicateContainerConflict={outboundTransportStore?.hasDuplicateContainerConflict}
          />
        </Grid>
      </Grid>
    )
  },
)

const TransportEditMode = observer(
  (props: {
    inboundTransportStore: ChangeTransportSubFormStore
    outboundTransportStore: ChangeTransportSubFormStore
    formStore: OrderFormStore
    disableInboundTransport?: boolean
  }) => {
    const { inboundTransportStore, outboundTransportStore, formStore, disableInboundTransport } =
      props
    const { t } = useTranslate()

    useMutualCarrierTypeCleanup(
      inboundTransportStore.carrierType,
      outboundTransportStore.carrierType,
    )

    return (
      <Grid container spacing={2} width={'100%'}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <OrderTransportCard
            title={t('inboundTransport', 'Inbound transport')}
            transportStore={inboundTransportStore}
            formStore={formStore}
            direction={CarrierVisitDirection.Inbound}
            hideContainerReleasePrompt
            isSmallView
            disabled={disableInboundTransport}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <OrderTransportCard
            title={t('outboundTransport', 'Outbound transport')}
            transportStore={outboundTransportStore}
            direction={CarrierVisitDirection.Outbound}
            formStore={formStore}
            isSmallView
          />
        </Grid>
      </Grid>
    )
  },
)

export const OrderTransportViewCard = observer(
  (
    props: OrderViewCardProps<OrderTransportViewCardAttributes> & {
      inboundTransportStore: ChangeTransportSubFormStore
      outboundTransportStore: ChangeTransportSubFormStore
      formStore?: OrderFormStore
      disableInboundTransport?: boolean
    },
  ) => {
    const {
      attributes,
      inboundTransportStore,
      outboundTransportStore,
      allowEdit,
      formStore,
      hideActions,
      onClickSave,
      onClickEdit,
      disableInboundTransport,
    } = props

    const { t } = useTranslate()

    const handleSave = async () => {
      const isTransportValid = validateCarrierTypes(
        inboundTransportStore?.carrierType,
        outboundTransportStore?.carrierType,
      )

      if (!isTransportValid) {
        return
      }

      if (onClickSave) {
        await onClickSave()
      }
    }

    return (
      <DataControlCard
        title={t('transport', 'Transport')}
        hideActions={hideActions}
        allowEdit={allowEdit}
        onClickEdit={onClickEdit}
        onClickSave={handleSave}
        dataCy='transport'
        viewComponent={
          <TransportViewMode
            attributes={attributes}
            inboundTransportStore={inboundTransportStore}
            outboundTransportStore={outboundTransportStore}
          />
        }
        editComponent={
          inboundTransportStore &&
          outboundTransportStore &&
          formStore && (
            <TransportEditMode
              inboundTransportStore={inboundTransportStore}
              outboundTransportStore={outboundTransportStore}
              formStore={formStore}
              disableInboundTransport={disableInboundTransport}
            />
          )
        }
      />
    )
  },
)
